import { useAuth } from '@/contexts/AuthContext';
import {
    authService,
    type GoogleAdsAccountsResponse,
} from '@common/api/authService';
import { useTranslation } from '@client-app/i18n';
import { Checkbox } from '@common/index';
import { CaretDown, Check, CircleNotch } from '@phosphor-icons/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    buildGoogleAdsRows,
    createGoogleAdsAccountRequestScope,
    getAccountName,
    getDisabledLabel,
    isSameGoogleAdsAccountRequestScope,
    normalizeCustomerId,
    type GoogleAdsAccountRequestScope,
    type GoogleAdsRow,
} from '@/pages/GoogleAdsAccountSelection/googleAdsAccountSelectionModel';
import { BusinessGroupSelectionList } from './BusinessGroupAssignmentModal';
import { type BrandSelectOption } from '../BrandSelectMenu';
import { createDefaultBusinessGroupAssignmentDraft } from './connectionBrandAssignmentFlow';
import {
    canReuseGoogleAdsAccountResponse,
    type GoogleAdsAccountResponseScope,
} from './googleAdsAccountResponseScope';
import Modal from '../Modal';

interface GoogleAdsAccountSelectionModalProps {
    isOpen: boolean;
    connectionId?: string | null;
    workspaceId?: string | null;
    initialResponse?: GoogleAdsAccountsResponse | null;
    initialResponseScope?: GoogleAdsAccountResponseScope | null;
    brandOptions?: BrandSelectOption[];
    /** Assign new Customer resources to the sole default group without a redundant second step. */
    autoAssignBusinessGroupId?: string | null;
    showBusinessGroupAssignment?: boolean;
    assignedBrandIdsByCustomerId?: Record<string, string[]>;
    assignedBrandAccessLevelsByCustomerId?: Record<
        string,
        Record<string, 'READ' | 'WRITE'>
    >;
    allowBusinessGroupAssignment?: boolean;
    onClose: () => void;
    onCancel?: () => void;
    onSaved?: (
        payload: GoogleAdsAccountSelectionSavedPayload,
    ) => void | Promise<void>;
}

export interface GoogleAdsAccountSelectionSavedPayload {
    integrationId: number | string;
    connectionId: string;
    selectedCustomerIds: string[];
    selectedCustomerBrandIds: Record<string, string[]>;
    selectedCustomerBrandAccessLevels: Record<
        string,
        Record<string, 'READ' | 'WRITE'>
    >;
}

type GoogleAdsSelectionStep = 'select-accounts' | 'assign-business-groups';

const getErrorMessage = (error: unknown, fallback: string) => {
    if (error instanceof Error) {
        return error.message;
    }
    return fallback;
};

const EMPTY_BRAND_OPTIONS: BrandSelectOption[] = [];
const EMPTY_ASSIGNED_BRAND_IDS_BY_CUSTOMER_ID: Record<string, string[]> = {};
const EMPTY_ASSIGNED_BRAND_ACCESS_LEVELS_BY_CUSTOMER_ID: Record<
    string,
    Record<string, 'READ' | 'WRITE'>
> = {};

interface ScopedGoogleAdsAccountsResponse {
    scope: GoogleAdsAccountRequestScope;
    value: GoogleAdsAccountsResponse;
}

interface GoogleAdsAccountSelectProps {
    accounts: GoogleAdsRow[];
    value: string | null;
    ariaLabel: string;
    disabled?: boolean;
    onChange: (customerId: string) => void;
}

const GoogleAdsAccountSelect = ({
    accounts,
    value,
    ariaLabel,
    disabled = false,
    onChange,
}: GoogleAdsAccountSelectProps) => {
    const [open, setOpen] = useState(false);
    const selectedAccount = accounts.find(
        (account) => normalizeCustomerId(account.id) === value,
    );

    return (
        <DropdownMenu.Root open={open} onOpenChange={setOpen}>
            <DropdownMenu.Trigger asChild>
                <button
                    type="button"
                    aria-label={ariaLabel}
                    disabled={disabled}
                    className="flex h-12 w-full items-center justify-between gap-2 rounded-[10px] border border-[#C8D0E2] bg-white px-3 text-left text-sm font-medium text-[#1A162F] outline-none transition-colors hover:border-[#98A2C0] focus-visible:border-[#1A162F] focus-visible:ring-2 focus-visible:ring-[#1A162F]/15 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
                >
                    <span className="min-w-0 truncate">
                        {selectedAccount
                            ? `${getAccountName(selectedAccount)} · ${normalizeCustomerId(selectedAccount.id)}`
                            : ''}
                    </span>
                    <CaretDown
                        size={14}
                        className="shrink-0 text-[#7C86A6]"
                    />
                </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    align="start"
                    sideOffset={6}
                    collisionPadding={8}
                    className="z-[10050] max-h-64 overflow-y-auto rounded-xl border border-[#E8EBF3] bg-white p-1.5 shadow-[0_12px_28px_rgba(23,29,56,0.16)]"
                    style={{
                        width: 'var(--radix-dropdown-menu-trigger-width)',
                        minWidth:
                            'var(--radix-dropdown-menu-trigger-width)',
                        maxWidth: 'calc(100vw - 16px)',
                    }}
                >
                    {accounts.map((account) => {
                        const customerId = normalizeCustomerId(account.id);
                        const isSelected = customerId === value;
                        return (
                            <DropdownMenu.Item
                                key={customerId}
                                onSelect={() => {
                                    onChange(customerId);
                                    setOpen(false);
                                }}
                                className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-sm outline-none transition-colors data-[highlighted]:bg-[#F4F5F8] data-[highlighted]:text-[#1A162F] ${
                                    isSelected
                                        ? 'bg-[#F4F5F8] text-[#1A162F]'
                                        : 'text-[#3D394D]'
                                }`}
                            >
                                <span className="min-w-0 truncate font-medium">
                                    {getAccountName(account)} · {customerId}
                                </span>
                                {isSelected ? (
                                    <Check
                                        size={16}
                                        weight="bold"
                                        className="shrink-0 text-[#1A162F]"
                                    />
                                ) : null}
                            </DropdownMenu.Item>
                        );
                    })}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
};

export default function GoogleAdsAccountSelectionModal({
    isOpen,
    connectionId,
    workspaceId,
    initialResponse = null,
    initialResponseScope = null,
    brandOptions = EMPTY_BRAND_OPTIONS,
    autoAssignBusinessGroupId = null,
    showBusinessGroupAssignment = true,
    assignedBrandIdsByCustomerId = EMPTY_ASSIGNED_BRAND_IDS_BY_CUSTOMER_ID,
    assignedBrandAccessLevelsByCustomerId = EMPTY_ASSIGNED_BRAND_ACCESS_LEVELS_BY_CUSTOMER_ID,
    allowBusinessGroupAssignment = true,
    onClose,
    onCancel,
    onSaved,
}: GoogleAdsAccountSelectionModalProps) {
    const { t } = useTranslation();
    const { currentWorkspace } = useAuth();
    const [scopedResponse, setScopedResponse] =
        useState<ScopedGoogleAdsAccountsResponse | null>(null);
    const [step, setStep] = useState<GoogleAdsSelectionStep>('select-accounts');
    const [selectedCustomerIds, setSelectedCustomerIds] = useState<Set<string>>(
        new Set(),
    );
    const [activeCustomerId, setActiveCustomerId] = useState<string | null>(
        null,
    );
    const [selectedCustomerBrandIds, setSelectedCustomerBrandIds] = useState<
        Record<string, string[]>
    >({});
    const [
        selectedCustomerBrandAccessLevels,
        setSelectedCustomerBrandAccessLevels,
    ] = useState<Record<string, Record<string, 'READ' | 'WRITE'>>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isSavingAccounts, setIsSavingAccounts] = useState(false);
    const [isSavingAssignments, setIsSavingAssignments] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const savedIntegrationIdRef = useRef<number | string | null>(null);
    const resolvedWorkspaceId = workspaceId || currentWorkspace?.id || null;
    const canReuseInitialResponse = canReuseGoogleAdsAccountResponse(
        initialResponse,
        initialResponseScope,
        {
            workspaceId: resolvedWorkspaceId,
            connectionId: connectionId || null,
        },
    );
    const requestGenerationRef = useRef(0);
    const activeRequestScopeRef = useRef<GoogleAdsAccountRequestScope | null>(
        null,
    );
    const response =
        scopedResponse &&
        activeRequestScopeRef.current &&
        scopedResponse.scope.workspaceId === resolvedWorkspaceId &&
        scopedResponse.scope.connectionId === (connectionId || null) &&
        isSameGoogleAdsAccountRequestScope(
            scopedResponse.scope,
            activeRequestScopeRef.current,
        )
            ? scopedResponse.value
            : null;
    const isSaving = isSavingAccounts || isSavingAssignments;

    const axiosRequestConfig = useMemo(
        () =>
            resolvedWorkspaceId
                ? { headers: { 'X-Workspace-Id': resolvedWorkspaceId } }
                : undefined,
        [resolvedWorkspaceId],
    );

    const resetFlow = useCallback(() => {
        savedIntegrationIdRef.current = null;
        setScopedResponse(null);
        setStep('select-accounts');
        setSelectedCustomerIds(new Set());
        setActiveCustomerId(null);
        setSelectedCustomerBrandIds({});
        setSelectedCustomerBrandAccessLevels({});
        setErrorMessage(null);
        setIsLoading(false);
        setIsSavingAccounts(false);
        setIsSavingAssignments(false);
    }, []);

    const loadAccounts = useCallback(async () => {
        if (!resolvedWorkspaceId || !isOpen) {
            return;
        }

        const requestScope = createGoogleAdsAccountRequestScope(
            resolvedWorkspaceId,
            connectionId,
            requestGenerationRef.current + 1,
        );
        requestGenerationRef.current = requestScope.generation;
        activeRequestScopeRef.current = requestScope;
        const isActiveRequest = () =>
            isSameGoogleAdsAccountRequestScope(
                activeRequestScopeRef.current,
                requestScope,
            );

        if (canReuseInitialResponse && initialResponse) {
            const defaultSelectedCustomerIds =
                initialResponse.defaultSelectedCustomerIds || [];
            setScopedResponse({ scope: requestScope, value: initialResponse });
            setSelectedCustomerIds(new Set(defaultSelectedCustomerIds));
            setActiveCustomerId(defaultSelectedCustomerIds[0] || null);
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);
        try {
            const nextResponse = await authService.getNangoGoogleAdsAccounts({
                connectionId: connectionId || undefined,
                axiosRequestConfig,
            });
            if (isActiveRequest()) {
                const defaultSelectedCustomerIds =
                    nextResponse.defaultSelectedCustomerIds || [];
                setScopedResponse({ scope: requestScope, value: nextResponse });
                setSelectedCustomerIds(new Set(defaultSelectedCustomerIds));
                setActiveCustomerId(defaultSelectedCustomerIds[0] || null);
            }
        } catch (error) {
            if (isActiveRequest()) {
                setErrorMessage(
                    getErrorMessage(
                        error,
                        t(
                            'website.integrationsSettings.googleAdsAccountSelection.updateFailed',
                        ),
                    ),
                );
            }
        } finally {
            if (isActiveRequest()) {
                setIsLoading(false);
            }
        }
    }, [
        axiosRequestConfig,
        connectionId,
        canReuseInitialResponse,
        initialResponse,
        isOpen,
        resolvedWorkspaceId,
        t,
    ]);

    useEffect(() => {
        if (!isOpen) {
            requestGenerationRef.current += 1;
            activeRequestScopeRef.current = null;
            resetFlow();
            return undefined;
        }

        void loadAccounts();

        return () => {
            requestGenerationRef.current += 1;
            activeRequestScopeRef.current = null;
        };
    }, [isOpen, loadAccounts, resetFlow]);

    const rows = useMemo<GoogleAdsRow[]>(
        () => buildGoogleAdsRows(response?.accounts || []),
        [response?.accounts],
    );
    const eligibleRows = useMemo(
        () => rows.filter((row) => row.eligible),
        [rows],
    );
    const eligibleCustomerIds = useMemo(
        () =>
            eligibleRows
                .map((row) => normalizeCustomerId(row.id))
                .filter(Boolean),
        [eligibleRows],
    );
    const selectedEligibleCustomerIds = useMemo(
        () =>
            eligibleCustomerIds.filter((customerId) =>
                selectedCustomerIds.has(customerId),
            ),
        [eligibleCustomerIds, selectedCustomerIds],
    );
    const savedAccountRows = useMemo(
        () =>
            rows.filter((row) =>
                selectedCustomerIds.has(normalizeCustomerId(row.id)),
            ),
        [rows, selectedCustomerIds],
    );
    const selectedEligibleCount = selectedEligibleCustomerIds.length;
    const areAllEligibleSelected =
        eligibleCustomerIds.length > 0 &&
        selectedEligibleCount === eligibleCustomerIds.length;
    const hasPartialEligibleSelection =
        selectedEligibleCount > 0 && !areAllEligibleSelected;
    const activeSelectedCustomerId =
        activeCustomerId && selectedCustomerIds.has(activeCustomerId)
            ? activeCustomerId
            : selectedEligibleCustomerIds[0] || null;
    // Keep the complete workspace Business-group list visible. Existing
    // Customer assignments are state that should be marked in the list, not
    // removed from it; filtering them out made the default group disappear
    // and left a fresh assignment draft unchecked.
    const activeBrandOptions = useMemo(() => brandOptions, [brandOptions]);
    const requiredActiveBrandIds = useMemo(
        () => activeBrandOptions
            .filter((businessGroup) => businessGroup.isDefault)
            .map((businessGroup) => businessGroup.id),
        [activeBrandOptions],
    );
    const lockedActiveBrandIds = useMemo(
        () =>
            Array.from(
                new Set([
                    ...requiredActiveBrandIds,
                    ...(activeSelectedCustomerId
                        ? assignedBrandIdsByCustomerId[activeSelectedCustomerId] || []
                        : []),
                ]),
            ),
        [
            activeSelectedCustomerId,
            assignedBrandIdsByCustomerId,
            requiredActiveBrandIds,
        ],
    );
    // This flow is additive: persisted Business groups remain visible but
    // must not be removed from the draft here.
    const hasBusinessGroupAssignments = selectedEligibleCustomerIds.some(
        (customerId) => (selectedCustomerBrandIds[customerId] || []).length > 0,
    );
    const hasAssignableBusinessGroups = selectedEligibleCustomerIds.some(
        () => brandOptions.length > 0,
    );

    const toggleCustomer = (customerId: string) => {
        const isSelected = selectedCustomerIds.has(customerId);
        setSelectedCustomerIds((previous) => {
            const next = new Set(previous);
            if (next.has(customerId)) {
                next.delete(customerId);
            } else {
                next.add(customerId);
            }
            return next;
        });
        setActiveCustomerId((previous) => {
            if (!isSelected) return customerId;
            if (previous !== customerId) return previous;
            return (
                selectedEligibleCustomerIds.find((id) => id !== customerId) ||
                null
            );
        });
    };

    const toggleAllEligible = () => {
        if (areAllEligibleSelected) {
            setSelectedCustomerIds(new Set());
            setActiveCustomerId(null);
            return;
        }
        setSelectedCustomerIds(new Set(eligibleCustomerIds));
        setActiveCustomerId((previous) =>
            previous && eligibleCustomerIds.includes(previous)
                ? previous
                : eligibleCustomerIds[0] || null,
        );
    };

    const toggleActiveCustomerBusinessGroup = (brandId: string) => {
        if (!activeSelectedCustomerId) return;
        if (lockedActiveBrandIds.includes(brandId)) return;

        setSelectedCustomerBrandIds((previous) => {
            const selectedBrandIds = previous[activeSelectedCustomerId] || [];
            const isAssigned = selectedBrandIds.includes(brandId);
            return {
                ...previous,
                [activeSelectedCustomerId]: isAssigned
                    ? selectedBrandIds.filter((id) => id !== brandId)
                    : [...selectedBrandIds, brandId],
            };
        });
        setSelectedCustomerBrandAccessLevels((previous) => {
            const currentLevels = previous[activeSelectedCustomerId] || {};
            const isAssigned = (
                selectedCustomerBrandIds[activeSelectedCustomerId] || []
            ).includes(brandId);
            return {
                ...previous,
                [activeSelectedCustomerId]: isAssigned
                    ? Object.fromEntries(
                          Object.entries(currentLevels).filter(
                              ([id]) => id !== brandId,
                          ),
                      )
                    : {
                          ...currentLevels,
                          [brandId]: currentLevels[brandId] || 'READ',
                      },
            };
        });
    };

    const updateActiveCustomerBusinessGroupAccess = (
        brandId: string,
        accessLevel: 'READ' | 'WRITE',
    ) => {
        if (!activeSelectedCustomerId) return;

        setSelectedCustomerBrandIds((previous) => {
            const selectedBrandIds = previous[activeSelectedCustomerId] || [];
            return selectedBrandIds.includes(brandId)
                ? previous
                : {
                      ...previous,
                      [activeSelectedCustomerId]: [
                          ...selectedBrandIds,
                          brandId,
                      ],
                  };
        });
        setSelectedCustomerBrandAccessLevels((previous) => ({
            ...previous,
            [activeSelectedCustomerId]: {
                ...previous[activeSelectedCustomerId],
                [brandId]: accessLevel,
            },
        }));
    };

    const saveAccounts = async () => {
        const resolvedConnectionId = response?.connectionId || connectionId;
        if (
            !resolvedWorkspaceId ||
            !response?.hasIntegration ||
            !resolvedConnectionId ||
            selectedEligibleCount === 0
        ) {
            return;
        }

        setIsSavingAccounts(true);
        setErrorMessage(null);
        try {
            const response = await authService.saveNangoGoogleAdsAccountSelection({
                connection_id: resolvedConnectionId,
                selected_customer_ids: selectedEligibleCustomerIds,
                axiosRequestConfig,
            });
            if (response.integrationId == null) {
                throw new Error(
                    t('website.integrationsSettings.googleAdsAccountSelection.updateFailed'),
                );
            }
            const savedIntegrationId = response.integrationId;
            savedIntegrationIdRef.current = savedIntegrationId;
            const initialBusinessGroupAssignmentsByCustomerId = Object.fromEntries(
                selectedEligibleCustomerIds.map((customerId) => {
                    const assignmentDraft = autoAssignBusinessGroupId
                        ? {
                              selectedBrandIds: [autoAssignBusinessGroupId],
                              accessLevels: { [autoAssignBusinessGroupId]: 'READ' as const },
                          }
                        : createDefaultBusinessGroupAssignmentDraft(
                              brandOptions,
                              assignedBrandIdsByCustomerId[customerId] || [],
                              assignedBrandAccessLevelsByCustomerId[customerId] || {},
                          );
                    return [customerId, assignmentDraft];
                }),
            );
            const initialBrandIdsByCustomerId = Object.fromEntries(
                Object.entries(initialBusinessGroupAssignmentsByCustomerId).map(
                    ([customerId, assignmentDraft]) => [
                        customerId,
                        assignmentDraft.selectedBrandIds,
                    ],
                ),
            );
            const initialBrandAccessLevelsByCustomerId = Object.fromEntries(
                Object.entries(initialBusinessGroupAssignmentsByCustomerId).map(
                    ([customerId, assignmentDraft]) => [
                        customerId,
                        assignmentDraft.accessLevels,
                    ],
                ),
            );
            setSelectedCustomerBrandIds(
                initialBrandIdsByCustomerId,
            );
            setSelectedCustomerBrandAccessLevels(
                initialBrandAccessLevelsByCustomerId,
            );
            setActiveCustomerId(selectedEligibleCustomerIds[0] || null);

            if (
                !showBusinessGroupAssignment ||
                !allowBusinessGroupAssignment ||
                autoAssignBusinessGroupId ||
                !hasAssignableBusinessGroups
            ) {
                await onSaved?.({
                    integrationId: savedIntegrationId,
                    connectionId: resolvedConnectionId,
                    selectedCustomerIds: selectedEligibleCustomerIds,
                    selectedCustomerBrandIds: initialBrandIdsByCustomerId,
                    selectedCustomerBrandAccessLevels:
                        initialBrandAccessLevelsByCustomerId,
                });
                onClose();
                return;
            }

            setStep('assign-business-groups');
        } catch (error) {
            setErrorMessage(
                getErrorMessage(
                    error,
                    t(
                        'website.integrationsSettings.googleAdsAccountSelection.updateFailed',
                    ),
                ),
            );
        } finally {
            setIsSavingAccounts(false);
        }
    };

    const finishBusinessGroupAssignments = async (
        keepDefaultBusinessGroupOnly = false,
    ) => {
        const resolvedConnectionId = response?.connectionId || connectionId;
        if (!resolvedConnectionId) return;
        const savedIntegrationId = savedIntegrationIdRef.current;
        if (savedIntegrationId == null) {
            setErrorMessage(
                t('website.integrationsSettings.googleAdsAccountSelection.updateFailed'),
            );
            return;
        }

        setIsSavingAssignments(true);
        setErrorMessage(null);
        try {
            const selectedBrandIdsByCustomerId = keepDefaultBusinessGroupOnly
                        ? Object.fromEntries(
                      selectedEligibleCustomerIds.map((customerId) => [
                          customerId,
                          createDefaultBusinessGroupAssignmentDraft(
                              brandOptions,
                              assignedBrandIdsByCustomerId[customerId] || [],
                              assignedBrandAccessLevelsByCustomerId[customerId] || {},
                          ).selectedBrandIds,
                      ]),
                  )
                : selectedCustomerBrandIds;
            const accessLevelsByCustomerId = keepDefaultBusinessGroupOnly
                        ? Object.fromEntries(
                      selectedEligibleCustomerIds.map((customerId) => [
                          customerId,
                          createDefaultBusinessGroupAssignmentDraft(
                              brandOptions,
                              assignedBrandIdsByCustomerId[customerId] || [],
                              assignedBrandAccessLevelsByCustomerId[customerId] || {},
                          ).accessLevels,
                      ]),
                  )
                : selectedCustomerBrandAccessLevels;

            await onSaved?.({
                integrationId: savedIntegrationId,
                connectionId: resolvedConnectionId,
                selectedCustomerIds: selectedEligibleCustomerIds,
                selectedCustomerBrandIds: Object.fromEntries(
                    selectedEligibleCustomerIds.map((customerId) => [
                        customerId,
                        selectedBrandIdsByCustomerId[customerId] || [],
                    ]),
                ),
                selectedCustomerBrandAccessLevels: Object.fromEntries(
                    selectedEligibleCustomerIds.map((customerId) => {
                        const selectedBrandIds =
                            selectedBrandIdsByCustomerId[customerId] || [];
                        const levels =
                            accessLevelsByCustomerId[customerId] || {};
                        return [
                            customerId,
                            Object.fromEntries(
                                selectedBrandIds.map((brandId) => [
                                    brandId,
                                    levels[brandId] || 'READ',
                                ]),
                            ),
                        ];
                    }),
                ),
            });
            onClose();
        } catch (error) {
            setErrorMessage(
                getErrorMessage(
                    error,
                    t(
                        'website.integrationsSettings.googleAdsAccountSelection.businessGroupAssignmentFailed',
                    ),
                ),
            );
        } finally {
            setIsSavingAssignments(false);
        }
    };

    const handleClose = () => {
        if (isSaving) return;
        if (step === 'assign-business-groups') {
            void finishBusinessGroupAssignments(true);
            return;
        }
        onCancel?.();
        onClose();
    };

    const modalTitle =
        step === 'select-accounts'
            ? t('website.integrationsSettings.googleAdsAccountSelection.title')
            : t(
                  'website.integrationsSettings.googleAdsAccountSelection.assignSavedAccountsTitle',
              );

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={modalTitle}
            width="max-w-4xl"
            bodyClassName="px-6 pb-6 pt-1"
            titleClassName="text-[#1A162F]"
        >
            <div className="space-y-4">
                {errorMessage ? (
                    <div className="rounded-[10px] border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {errorMessage}
                    </div>
                ) : null}

                {step === 'select-accounts' ? (
                    <>
                        <p className="text-sm leading-6 text-[#667085]">
                            {t(
                                'website.integrationsSettings.googleAdsAccountSelection.selectAccountsDescription',
                            )}
                        </p>
                        <div className="overflow-hidden rounded-xl border border-[#E8EBF3] bg-white">
                            {isLoading ? (
                                <div className="flex min-h-[360px] items-center justify-center text-sm text-[#667085]">
                                    <CircleNotch
                                        size={18}
                                        className="mr-2 animate-spin"
                                    />
                                    {t(
                                        'website.integrationsSettings.googleAdsAccountSelection.loadingAccounts',
                                    )}
                                </div>
                            ) : !response?.hasIntegration ? (
                                <div className="flex min-h-[360px] items-center justify-center px-6 text-center text-sm text-[#667085]">
                                    {t(
                                        'website.integrationsSettings.googleAdsAccountSelection.noActiveConnection',
                                    )}
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-center gap-3 border-b border-[#E8EBF3] bg-[#F8F9FC] px-4 py-3 text-xs font-medium text-[#666F8D]">
                                        <Checkbox
                                            checked={
                                                hasPartialEligibleSelection
                                                    ? 'indeterminate'
                                                    : areAllEligibleSelected
                                            }
                                            disabled={
                                                isSaving ||
                                                eligibleCustomerIds.length === 0
                                            }
                                            aria-label={t(
                                                'website.integrationsSettings.googleAdsAccountSelection.selectAllCustomers',
                                            )}
                                            onCheckedChange={toggleAllEligible}
                                        />
                                        <span>
                                            {t(
                                                'website.integrationsSettings.googleAdsAccountSelection.customer',
                                            )}
                                        </span>
                                    </div>
                                    <div className="integrations-apps-scroll-area max-h-[min(480px,calc(55vh-40px))] divide-y divide-[#E8EBF3] overflow-y-auto">
                                        {rows.map((account) => {
                                            const customerId =
                                                normalizeCustomerId(account.id);
                                            const isSelected =
                                                selectedCustomerIds.has(
                                                    customerId,
                                                );
                                            return (
                                                <button
                                                    key={customerId}
                                                    type="button"
                                                    disabled={
                                                        !account.eligible ||
                                                        isSaving
                                                    }
                                                    onClick={() =>
                                                        toggleCustomer(
                                                            customerId,
                                                        )
                                                    }
                                                    className={`flex w-full min-w-0 items-center gap-3 px-4 py-3 text-left transition-colors ${isSelected ? 'bg-[#F4F6FA]' : 'bg-white hover:bg-[#FCFCFD]'} disabled:cursor-not-allowed disabled:opacity-60`}
                                                >
                                                    <span
                                                        onClick={(event) =>
                                                            event.stopPropagation()
                                                        }
                                                    >
                                                        <Checkbox
                                                            checked={isSelected}
                                                            disabled={
                                                                !account.eligible ||
                                                                isSaving
                                                            }
                                                            onCheckedChange={() =>
                                                                toggleCustomer(
                                                                    customerId,
                                                                )
                                                            }
                                                            aria-label={getAccountName(
                                                                account,
                                                            )}
                                                        />
                                                    </span>
                                                    <span className="min-w-0">
                                                        <span className="block truncate text-sm font-medium text-[#1A162F]">
                                                            {getAccountName(
                                                                account,
                                                            )}
                                                        </span>
                                                        <span className="mt-0.5 block truncate text-xs text-[#667085]">
                                                            {t(
                                                                'website.integrationsSettings.googleAdsAccountSelection.customerId',
                                                            )}
                                                            : {customerId}
                                                        </span>
                                                        <span className="mt-0.5 block truncate text-xs text-[#667085]">
                                                            {account.currency_code ||
                                                                '-'}{' '}
                                                            /{' '}
                                                            {account.time_zone ||
                                                                account.timezone ||
                                                                '-'}
                                                        </span>
                                                        {!account.eligible ? (
                                                            <span className="mt-1 block text-xs text-[#98A2B3]">
                                                                {getDisabledLabel(
                                                                    account,
                                                                )}
                                                            </span>
                                                        ) : null}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                        {eligibleRows.length === 0 ? (
                                            <div className="px-4 py-12 text-center text-sm text-[#667085]">
                                                {t(
                                                    'website.integrationsSettings.googleAdsAccountSelection.noEligibleAccounts',
                                                )}
                                            </div>
                                        ) : null}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="flex items-center justify-between gap-4 pt-1">
                            <span className="text-sm text-[#667085]">
                                {t(
                                    'website.integrationsSettings.googleAdsAccountSelection.selectedAccountsCount',
                                    {
                                        count: selectedEligibleCount,
                                    },
                                )}
                            </span>
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    disabled={isSaving}
                                    className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#666F8D] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {t('common.cancel')}
                                </button>
                                <button
                                    type="button"
                                    onClick={saveAccounts}
                                    disabled={
                                        isLoading ||
                                        isSaving ||
                                        !response?.hasIntegration ||
                                        selectedEligibleCount === 0
                                    }
                                    className="inline-flex items-center justify-center gap-2 rounded-[10px] bg-[#1A162F] px-5 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {isSavingAccounts ? (
                                        <CircleNotch
                                            size={14}
                                            className="animate-spin"
                                        />
                                    ) : null}
                                    <span>
                                        {t(
                                            'website.integrationsSettings.googleAdsAccountSelection.saveAccounts',
                                            { count: selectedEligibleCount },
                                        )}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="text-sm leading-6 text-[#667085]">
                            {t(
                                'website.integrationsSettings.googleAdsAccountSelection.selectSavedAccountDescription',
                            )}
                        </p>
                        <div className="rounded-xl border border-[#E8EBF3] bg-white p-5">
                            <label className="block">
                                <span className="mb-2 block text-sm font-medium text-[#1A162F]">
                                    {t(
                                        'website.integrationsSettings.googleAdsAccountSelection.savedAccount',
                                    )}
                                </span>
                                {savedAccountRows.length > 1 ? (
                                    <GoogleAdsAccountSelect
                                        accounts={savedAccountRows}
                                        value={activeSelectedCustomerId || ''}
                                        disabled={isSaving}
                                        ariaLabel={t(
                                            'website.integrationsSettings.googleAdsAccountSelection.savedAccount',
                                        )}
                                        onChange={setActiveCustomerId}
                                    />
                                ) : (
                                    <div className="flex h-12 items-center rounded-[10px] border border-[#C8D0E2] bg-[#F8F9FC] px-3 text-sm font-medium text-[#1A162F]">
                                        {savedAccountRows[0]
                                            ? `${getAccountName(savedAccountRows[0])} · ${normalizeCustomerId(savedAccountRows[0].id)}`
                                            : ''}
                                    </div>
                                )}
                            </label>

                            <div className="mt-6 border-t border-[#E8EBF3] pt-5">
                                <h3 className="text-base font-medium text-[#1A162F]">
                                    {t(
                                        'website.integrationsSettings.googleAdsAccountSelection.businessGroupsForAccount',
                                    )}
                                </h3>
                                <div className="mt-4">
                                    {activeSelectedCustomerId ? (
                                        <BusinessGroupSelectionList
                                            brands={activeBrandOptions}
                                            selectedBrandIds={
                                                selectedCustomerBrandIds[
                                                    activeSelectedCustomerId
                                                ] || []
                                            }
                                            assignedBrandIds={
                                                assignedBrandIdsByCustomerId[
                                                    activeSelectedCustomerId
                                                ] || []
                                            }
                                            accessLevels={
                                                selectedCustomerBrandAccessLevels[
                                                    activeSelectedCustomerId
                                                ] || {}
                                            }
                                            onToggle={
                                                toggleActiveCustomerBusinessGroup
                                            }
                                            onAccessLevelChange={
                                                updateActiveCustomerBusinessGroupAccess
                                            }
                                            requiredBrandIds={lockedActiveBrandIds}
                                            disabled={isSaving}
                                        />
                                    ) : null}
                                </div>
                            </div>
                        </div>

                        <div
                            className={`flex items-center gap-4 pt-1 ${
                                selectedEligibleCount > 1
                                    ? 'justify-between'
                                    : 'justify-end'
                            }`}
                        >
                            {selectedEligibleCount > 1 ? (
                                <span className="text-sm text-[#667085]">
                                    {t(
                                        'website.integrationsSettings.googleAdsAccountSelection.assignmentProgress',
                                        {
                                            assigned:
                                                selectedEligibleCustomerIds.filter(
                                                    (customerId) =>
                                                        (
                                                            selectedCustomerBrandIds[
                                                                customerId
                                                            ] || []
                                                        ).length > 0,
                                                ).length,
                                            count: selectedEligibleCount,
                                        },
                                    )}
                                </span>
                            ) : null}
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() =>
                                        void finishBusinessGroupAssignments(true)
                                    }
                                    disabled={isSaving}
                                    className="rounded-[10px] px-4 py-2.5 text-sm font-medium text-[#666F8D] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {t(
                                        'website.integrationsSettings.keepDefaultBusinessGroupOnly',
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() =>
                                        void finishBusinessGroupAssignments()
                                    }
                                    disabled={
                                        isSaving || !hasBusinessGroupAssignments
                                    }
                                    className="inline-flex items-center justify-center gap-2 rounded-[10px] bg-[#1A162F] px-5 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {isSavingAssignments ? (
                                        <CircleNotch
                                            size={14}
                                            className="animate-spin"
                                        />
                                    ) : null}
                                    <span>
                                        {t(
                                            'website.integrationsSettings.googleAdsAccountSelection.saveAssignments',
                                        )}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
}
